// EVERY SCREEN IS ITS OWN CHUNK.
//
// This file used to import all 168 screens at the top, so the build put the
// whole product into one file: 12.87 MB of JavaScript, uncompressed, that a
// guest's phone had to download before a QR menu could draw a single dish.
// The POS, every report, the setup wizards - all of it, to show a menu.
//
// React.lazy turns each one into a separate file, fetched when that screen is
// actually opened - the way a video arrives in pieces rather than whole. What
// stays in the first download is only what the first paint needs: the router,
// the login, the guest QR page and the admin shell.
//
// Two rules for anyone adding a screen:
//   1. `const X = lazy(() => import('./admin/components/X'));` - a plain
//      import puts that screen back into the first download.
//   2. Nothing outside routing may import from a lazily-loaded file. A named
//      export pulled in elsewhere (AdminLayout does this for four tab-strip
//      helpers) drags that whole module back into the main chunk.
import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import RouteSkeleton from './admin/components/RouteSkeleton';

import axios from 'axios';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SetupGuard from './SetupGuard';
import AdminLayout from './admin/AdminLayout';
import CompanyGate from './admin/CompanyGate';
import { PermissionProvider } from './admin/rbac';
import { ModuleLayout } from './ui';
import Login from './admin/Login';
import DirectLogin from './admin/DirectLogin';
import DeleteAccount from './admin/DeleteAccount';
import config from './config';
import QrPageLoader from './admin/QrPageLoader';
const BanquetReserve = lazy(() => import('./admin/components/BanquetReserve'));
const BanquetPackage = lazy(() => import('./admin/components/BanquetPackage'));
const BillPrintSettings = lazy(() => import('./admin/components/BillPrintSettings'));
const Dashboard = lazy(() => import('./admin/components/Dashboard'));
const ResturantDashbord = lazy(() => import('./admin/components/ResturantDashbord'));
const DashboardSwitcher = lazy(() => import('./admin/components/DashboardSwitcher'));
const HotelList = lazy(() => import('./admin/components/HotelList'));
const AddHotel = lazy(() => import('./admin/components/AddHotel'));
const RoomList = lazy(() => import('./admin/components/RoomList'));
const AddRoom = lazy(() => import('./admin/components/AddRoom'));
const ManageInventory = lazy(() => import('./admin/components/ManageInventory'));
const ManagePackage = lazy(() => import('./admin/components/ManagePackage'));
const RoomTypeMaster = lazy(() => import('./admin/components/RoomTypeMaster'));
const AddBanquetHall = lazy(() => import('./admin/components/AddBanquetHall'));
const Availbleroom = lazy(() => import('./admin/components/RoomAvailable'));
const BookingReport = lazy(() => import('./admin/components/Bookings'));
const PackageReport = lazy(() => import('./admin/components/PackageReport'));
const InvoicePage = lazy(() => import('./admin/components/InvoicePage'));
const BanquetInvoice = lazy(() => import('./admin/components/BanquetInvoice'));
const Logout = lazy(() => import('./admin/components/Logout'));
const PettyCashManagement = lazy(() => import('./admin/components/PettyCashManagement'));
const Addonm = lazy(() => import('./admin/components/Addonmaster'));
const Paymentreport = lazy(() => import('./admin/components/PaymentLedgerReport'));
const RestaurantPayments = lazy(() => import('./admin/components/RestaurantPayments'));
const QrOrders = lazy(() => import('./admin/components/QrOrders'));
const IndentEntry = lazy(() => import('./admin/components/IndentEntry'));
const IndentList = lazy(() => import('./admin/components/IndentList'));
const AmountCorrection = lazy(() => import('./admin/components/AmountCorrection'));
const Bookingfilter = lazy(() => import('./admin/components/BookingReportPage'));
const OtaBookings = lazy(() => import('./admin/components/OtaBookings'));
const AddonProfitReport = lazy(() => import('./admin/components/Addonprofit'));
const InvoiceReport = lazy(() => import('./admin/components/InvoiceReport'));
const NightAuditReport = lazy(() => import('./admin/components/NightAuditReport'));
const OccupancyReport = lazy(() => import('./admin/components/OccupancyReport'));
const BanquetReport = lazy(() => import('./admin/components/BanquetReport'));
const BanquetCalendar = lazy(() => import('./admin/components/BanquetCalendar'));
const SettlementReport = lazy(() => import('./admin/components/SettlementReport'));
const SaleReportPerDay = lazy(() => import('./admin/components/SaleReportPerDay'));
const GstReports = lazy(() => import('./admin/components/GstReports'));
const RestaurantSaleReport = lazy(() => import('./admin/components/RestaurantSaleReport'));
const ActivityLogReport = lazy(() => import('./admin/components/ActivityLogReport'));
const HrDepartments = lazy(() => import('./admin/components/Hr/Departments'));
const HrDesignations = lazy(() => import('./admin/components/Hr/Designations'));
const HrEmployees = lazy(() => import('./admin/components/Hr/Employees'));
const HrShifts = lazy(() => import('./admin/components/Hr/Shifts'));
const HrAttendance = lazy(() => import('./admin/components/Hr/Attendance'));
const HrLeaveTypes = lazy(() => import('./admin/components/Hr/LeaveTypes'));
const HrLeaveRequests = lazy(() => import('./admin/components/Hr/LeaveRequests'));
const HrOvertime = lazy(() => import('./admin/components/Hr/Overtime'));
const HrAdvanceSalary = lazy(() => import('./admin/components/Hr/AdvanceSalary'));
const HrLoans = lazy(() => import('./admin/components/Hr/Loans'));
const HrSalaryStructure = lazy(() => import('./admin/components/Hr/SalaryStructure'));
const HrPayroll = lazy(() => import('./admin/components/Hr/Payroll'));
const HrPayslip = lazy(() => import('./admin/components/Hr/Payslip'));
const HrSalaryPayments = lazy(() => import('./admin/components/Hr/SalaryPayments'));
const HrServiceCharge = lazy(() => import('./admin/components/Hr/ServiceCharge'));
const HrTips = lazy(() => import('./admin/components/Hr/Tips'));
const HrStaffMeals = lazy(() => import('./admin/components/Hr/StaffMeals'));
const HrUniformDeposit = lazy(() => import('./admin/components/Hr/UniformDeposit'));
const HrDashboard = lazy(() => import('./admin/components/Hr/HrDashboard'));
const HrReports = lazy(() => import('./admin/components/Hr/HrReports'));
const Guestreport = lazy(() => import('./admin/components/Guestreport'));
const CreateUser = lazy(() => import('./admin/components/CreateUser'));
const UserPermission = lazy(() => import('./admin/components/UserPermission'));
const UserReport = lazy(() => import('./admin/components/UserReport'));
const RestaurantInvoiceReport = lazy(() => import('./admin/components/RestaurantInvoiceReport'));
const RestaurantActivity = lazy(() => import('./admin/components/RestaurantActivity'));
const RestroReportsLayout = lazy(() => import('./admin/components/RestroReportsLayout'));
const ZReport = lazy(() => import('./admin/components/ZReport'));
const PrinterHealth = lazy(() => import('./admin/components/PrinterHealth'));
const MenuUploadHistory = lazy(() => import('./admin/components/MenuUploadHistory'));
const OnlineOrdering = lazy(() => import('./admin/components/OnlineOrdering'));
const OnlineOrdersPage = lazy(() => import('./admin/components/OnlineOrders').then((m) => ({ default: m.OnlineOrdersPage })));
const AccountingIntegration = lazy(() => import('./admin/components/AccountingIntegration'));
const MarketingCustomers = lazy(() => import('./admin/components/MarketingCustomers'));
const MarketingSettings = lazy(() => import('./admin/components/MarketingSettings'));
const MarketingLoyalty = lazy(() => import('./admin/components/MarketingLoyalty'));
const MarketingCampaigns = lazy(() => import('./admin/components/MarketingCampaigns'));
const MarketingAutomations = lazy(() => import('./admin/components/MarketingAutomations'));
const MarketingDashboard = lazy(() => import('./admin/components/MarketingDashboard'));
const MultiHotelDashboard = lazy(() => import('./admin/components/MultiHotelDashboard'));
const StockTransfer = lazy(() => import('./admin/components/StockTransfer'));
const StockOpeningClosingReport = lazy(() => import('./admin/components/StockOpeningClosingReport'));
const OrderHistory = lazy(() => import('./admin/components/OrderHistory'));
const BookingHistory = lazy(() => import('./admin/components/BookingHistory'));
const RestaurantGuestReport = lazy(() => import('./admin/components/RestaurantGuestReport'));
const CustomerDueReport = lazy(() => import('./admin/components/CustomerDueReport'));
const SuperAdminCompanies = lazy(() => import('./admin/components/SuperAdminCompanies'));
const Reservations = lazy(() => import('./admin/components/Reservations'));
const MenuMaster = lazy(() => import('./admin/components/MenuMaster'));
const CompanySettings = lazy(() => import('./admin/components/CompanySettings'));
const BusinessSettings = lazy(() => import('./admin/components/BusinessSettings'));
const LanguageSettings = lazy(() => import('./admin/components/LanguageSettings'));
const FrontDeskToday = lazy(() => import('./admin/components/FrontDeskToday'));
const ReceptionToday = lazy(() => import('./admin/components/ReceptionToday'));
const RoomGrid = lazy(() => import('./admin/components/RoomGrid'));
const Dormitories = lazy(() => import('./admin/components/Dormitories'));
const BedMap = lazy(() => import('./admin/components/BedMap'));
const GuestProfile = lazy(() => import('./admin/components/GuestProfile'));
const SupportTickets = lazy(() => import('./admin/components/SupportTickets'));
const SupportAdmin = lazy(() => import('./admin/components/SupportAdmin'));
const RbacRoleManagement = lazy(() => import('./admin/components/RbacRoleManagement'));
const RbacUserOverrides = lazy(() => import('./admin/components/RbacUserOverrides'));
const Agent = lazy(() => import('./admin/components/Agent'));
const AgentReport = lazy(() => import('./admin/components/AgentReport'));
const AgentCommision = lazy(() => import('./admin/components/AgentCommision'));
const Couponmaster = lazy(() => import('./admin/components/Couponmaster'));
const Addonreport = lazy(() => import('./admin/components/Addonreport'));
const RoomBookingWizard = lazy(() => import('./admin/components/RoomBookingWizard'));
const BulkBookings = lazy(() => import('./admin/components/bulkbookings'));
const PurchaseEntry = lazy(() => import('./admin/components/PurchaseEntry'));
const FrontWebsite = lazy(() => import('./admin/components/FrontWebsite'));
const PurchaseLedgerReport = lazy(() => import('./admin/components/PurchaseLedgerReport'));
const PubCardRequests = lazy(() => import('./admin/components/PubCardRequests'));
const PubCardLayout = lazy(() => import('./admin/components/PubCardLayout'));
const PubCardReport = lazy(() => import('./admin/components/PubCardReport'));
const PubCardLedger = lazy(() => import('./admin/components/PubCardLedger'));
const StorePurchase = lazy(() => import('./admin/components/StorePurchase'));
const RecipeMaster = lazy(() => import('./admin/components/RecipeMaster'));
const SupplierMaster = lazy(() => import('./admin/components/SupplierMaster'));
const ChannelManager = lazy(() => import('./admin/components/ChannelManager'));
const ResetData = lazy(() => import('./admin/components/ResetData'));
const StoreLocations = lazy(() => import('./admin/components/StoreLocations'));
const RoomReservation = lazy(() => import('./admin/components/RoomReservation'));
const StockDashboard = lazy(() => import('./admin/components/StockDashboard'));
const PurchaseReports = lazy(() => import('./admin/components/PurchaseReports'));
const HoldRoom = lazy(() => import('./admin/components/HoldRoom'));
const Holdreport = lazy(() => import('./admin/components/Holdreport'));
const Category = lazy(() => import('./admin/components/Category'));
const AddItem = lazy(() => import('./admin/components/AddItem'));
const CreateOrder = lazy(() => import('./admin/components/CreateOrder'));
const AreaPricing = lazy(() => import('./admin/components/AreaPricing'));
const KotReport = lazy(() => import('./admin/components/KotReport'));
const HourlyBooking = lazy(() => import('./admin/components/HourlyBooking'));
const Order = lazy(() => import('./admin/components/Order'));
const RestroInvoice = lazy(() => import('./admin/components/RestroInvoice'));
const Changeroom = lazy(() => import('./admin/components/Changeroom'));
const Calender = lazy(() => import('./admin/components/Calender'));
const ItemReport = lazy(() => import('./admin/components/ItemReport'));
const ExpiryReport = lazy(() => import('./admin/components/ExpiryReport'));
const AllCategory = lazy(() => import('./admin/components/AllCategory'));
const Createarea = lazy(() => import('./admin/components/Createarea'));
const TableOrders = lazy(() => import('./admin/components/TableOrders'));
const QrCodeGenerator = lazy(() => import('./admin/components/QrCodeGenerator'));
const BookingLedger = lazy(() => import('./admin/components/BookingLedger'));
const AddDepartment = lazy(() => import('./admin/components/AddDepartment'));
const HotelSetupWizard = lazy(() => import('./admin/components/HotelSetupWizard'));
const HotelSetupWizardIndia = lazy(() => import('./admin/components/HotelSetupWizardIndia'));
const Customerwallet = lazy(() => import('./admin/components/Customerwallet'));
const ChangePackage = lazy(() => import('./admin/components/ChangePackage'));
const KitchenPendingOrders = lazy(() => import('./admin/components/KitchenPendingOrders'));
const VariantManagement = lazy(() => import('./admin/components/VariantManagement'));
const ItemStoreMapping = lazy(() => import('./admin/components/ItemStoreMapping'));
const StoreMaster = lazy(() => import('./admin/components/StoreMaster'));
const RestaurantMastersLayout = lazy(() => import('./admin/components/RestaurantMastersLayout'));
const RestaurantOrdersLayout = lazy(() => import('./admin/components/RestaurantOrdersLayout'));
const StockTransactionHistory = lazy(() => import('./admin/components/StockTransactionHistory'));
// ---- New modules: Spa / Laundry / MiniBar / Housekeeping ----
const SpaServiceMaster = lazy(() => import('./admin/components/SpaServiceMaster'));
const TherapistMaster = lazy(() => import('./admin/components/TherapistMaster'));
const SpaBookings = lazy(() => import('./admin/components/SpaBookings'));
const LaundryItemMaster = lazy(() => import('./admin/components/LaundryItemMaster'));
const LaundryOrders = lazy(() => import('./admin/components/LaundryOrders'));
const MiniBarItemMaster = lazy(() => import('./admin/components/MiniBarItemMaster'));
const MiniBarConsumption = lazy(() => import('./admin/components/MiniBarConsumption'));
const HousekeepingStaffMaster = lazy(() => import('./admin/components/HousekeepingStaffMaster'));
const HousekeepingTasks = lazy(() => import('./admin/components/HousekeepingTasks'));
const HousekeepingDashboard = lazy(() => import('./admin/components/HousekeepingDashboard'));
const Agentcommissionreport = lazy(() => import('./admin/components/Agentcommissionreport'));
// ---- SaaS Subscription module ----
const Subscription = lazy(() => import('./admin/components/Subscription'));
const SubscriptionPlans = lazy(() => import('./admin/components/SubscriptionPlans'));
const SubscriptionPaymentReport = lazy(() => import('./admin/components/SubscriptionPaymentReport'));
// ------------------ Protected Route ------------------
// Setup-check is handled in Login.js (API-based).
// ProtectedRoute only guards against unauthenticated access.
function ProtectedRoute({ children, skipSetupCheck = false }) {
  const user = sessionStorage.getItem('user');
  if (!user) return <Navigate to='/login' replace />;

  // Fallback: if somehow an unauthenticated user reaches admin without a
  // hotel (e.g. direct URL), send them to setup.
  if (!skipSetupCheck && !localStorage.getItem('hotelSetupDone')) {
    return <Navigate to='/setup' replace />;
  }
  return children;
}

export default function App() {
  const navigate = useNavigate();

  return (
    // PermissionProvider is what makes every screen's requirePerm /
    // hasPermission real. It was exported from ./admin/rbac but never
    // mounted, so usePermission() fell back to "allow everything" on every
    // screen: a switch turned OFF on User Permissions could hide a PAGE (the
    // sidebar reads sessionStorage directly) but never a BUTTON.
    <PermissionProvider>
      <ToastContainer position='top-right' autoClose={3000} theme='light' />

      {/* The moment between the click and the screen, now that each screen is
          fetched on demand. A skeleton shaped like the screen that is coming,
          not a spinner: one says "on its way", the other says "something is
          wrong". */}
      <Suspense fallback={<RouteSkeleton />}>
      <Routes>
        <Route
          path='/'
          element={
            <ProtectedRoute>
              <Navigate to='/admin' replace />
            </ProtectedRoute>
          }
        />

        <Route path='/login' element={<Login />} />
        <Route path='/direct-login' element={<DirectLogin />} />
        <Route path='/logout' element={<Logout />} />

        {/* Public account-deletion request, required by the app stores.
            Outside ProtectedRoute and outside /admin on purpose: a locked-out
            user must still reach it, and a store reviewer opens it with no
            login. It is not in fullMenuStructure, so it never appears in the
            sidebar. */}
        <Route path='/delete-account' element={<DeleteAccount />} />
        {/* Hotel Setup Wizard - international */}
        <Route
          path='/setup'
          element={
            <ProtectedRoute skipSetupCheck={true}>
              <SetupGuard>
                <HotelSetupWizard />
              </SetupGuard>
            </ProtectedRoute>
          }
        />

        <Route
          path='/setup/india'
          element={
            <ProtectedRoute skipSetupCheck={true}>
              <SetupGuard>
                <HotelSetupWizardIndia />
              </SetupGuard>
            </ProtectedRoute>
          }
        />

        <Route path='/invoice/:bookingId' element={<InvoicePage />} />
        <Route path='/banquet-invoice/:bookingId' element={<BanquetInvoice />} />

        <Route path='/RestroInvoice/:orderId' element={<RestroInvoice />} />
        <Route path='/order' element={<QrPageLoader />} />
        <Route
          path='/admin/*'
          element={
            <ProtectedRoute>
              {/* The company is loaded ONCE, here, before anything below it
                  renders. AdminLayout and the screens under it read
                  companyDetails / companyId / propertType straight out of
                  sessionStorage while they render, and sessionStorage does not
                  re-render anyone when it changes - so they have to find it
                  already written. This is what replaced the Dashboard's
                  window.location.reload(). */}
              <CompanyGate>
                <AdminLayout />
              </CompanyGate>
            </ProtectedRoute>
          }>
          {/* <Route index element={<Dashboard />} /> */}
          <Route index element={<DashboardSwitcher />} />
          <Route path='dashboard' element={<DashboardSwitcher />} />

          {/* Front Desk module - compact header + horizontal tabs. Paths UNCHANGED. */}
          <Route element={<ModuleLayout moduleKey='frontdesk' />}>
            <Route path='BookingReport' element={<BookingReport />} />
            <Route path='Bookingfilter' element={<Bookingfilter />} />
            <Route path='OtaBookings' element={<OtaBookings />} />
            {/* Reservations tab -> timeline calendar. Separate path (not in the
                sidebar) so it inherits the front-desk permission and never gets
                blocked by a missing 'Calender' grant. */}
            <Route path='ReservationCalendar' element={<Calender />} />
            <Route path='Calender' element={<Calender />} />
            <Route path='Changeroom' element={<Changeroom />} />
            <Route path='HoldRoom' element={<HoldRoom />} />
            <Route path='BulkBookings' element={<BulkBookings />} />
          </Route>
          <Route path='SuperAdminCompanies' element={<SuperAdminCompanies />} />
          {/* Quick Booking now uses the SAME premium New-Booking layout
              (ManageInventory) - hotel/date card, Private Room / Dormitory
              toggle, floor-wise room grid - instead of the old 3-step wizard. */}
          <Route path='QuickBooking' element={<ManageInventory />} />
          <Route path='MenuMaster' element={<MenuMaster />} />
          <Route path='CompanySettings' element={<CompanySettings />} />
          <Route path='BusinessSettings' element={<BusinessSettings />} />
          <Route path='LanguageSettings' element={<LanguageSettings />} />
          {/* One component, two routes - arrivals and departures differ only in
              which side of the stay they list. */}
          <Route path='ReceptionToday' element={<ReceptionToday />} />
          <Route path='RoomGrid' element={<RoomGrid />} />
          <Route path='GuestProfile' element={<GuestProfile />} />
          <Route path='Arrivals' element={<FrontDeskToday mode='arrivals' />} />
          <Route path='Departures' element={<FrontDeskToday mode='departures' />} />
          <Route path='SupportTickets' element={<SupportTickets />} />
          <Route path='SupportAdmin' element={<SupportAdmin />} />
          <Route path='resturantdashbord' element={<ResturantDashbord />} />
          <Route path='hotels' element={<HotelList />} />
          <Route path='hotels/add' element={<AddHotel />} />
          <Route path='rooms' element={<RoomList />} />
          <Route path='rooms/add' element={<AddRoom />} />
          <Route path='Dormitories' element={<Dormitories />} />
          <Route path='BedMap' element={<BedMap />} />
          <Route path='roomtype' element={<RoomTypeMaster />} />
          <Route path='manageinventory' element={<ManageInventory />} />
          <Route path='managepackage' element={<ManagePackage />} />
          <Route path='banquethall/add' element={<AddBanquetHall />} />
          <Route path='banquethall/package' element={<BanquetPackage />} />
          <Route path='availableroom' element={<Availbleroom />} />
          <Route path='packagereport' element={<PackageReport />} />
          <Route path='PettyCashManagement' element={<PettyCashManagement />} />
          <Route path='Addonm' element={<Addonm />} />
          <Route path='Paymentreport' element={<Paymentreport />} />
          <Route path='IndentEntry' element={<IndentEntry />} />
          <Route path='IndentList' element={<IndentList />} />
          <Route path='AmountCorrection' element={<AmountCorrection />} />
          <Route path='AddonProfitReport' element={<AddonProfitReport />} />
          <Route path='InvoiceReport' element={<InvoiceReport />} />
          <Route path='NightAuditReport' element={<NightAuditReport />} />
          <Route path='OccupancyReport' element={<OccupancyReport />} />
          <Route path='BanquetReport' element={<BanquetReport />} />
          <Route path='BanquetCalendar' element={<BanquetCalendar />} />
          <Route path='BanquetReserve' element={<BanquetReserve />} />
          <Route path='SettlementReport' element={<SettlementReport />} />
          <Route path='SaleReportPerDay' element={<SaleReportPerDay />} />
          <Route path='GstReports' element={<GstReports />} />
          <Route path='ActivityLog' element={<ActivityLogReport />} />
          <Route path='Hr/Departments' element={<HrDepartments />} />
          <Route path='Hr/Designations' element={<HrDesignations />} />
          <Route path='Hr/Employees' element={<HrEmployees />} />
          <Route path='Hr/Shifts' element={<HrShifts />} />
          <Route path='Hr/Attendance' element={<HrAttendance />} />
          <Route path='Hr/LeaveTypes' element={<HrLeaveTypes />} />
          <Route path='Hr/LeaveRequests' element={<HrLeaveRequests />} />
          <Route path='Hr/Overtime' element={<HrOvertime />} />
          <Route path='Hr/AdvanceSalary' element={<HrAdvanceSalary />} />
          <Route path='Hr/Loans' element={<HrLoans />} />
          <Route path='Hr/SalaryStructure' element={<HrSalaryStructure />} />
          <Route path='Hr/Payroll' element={<HrPayroll />} />
          <Route path='Hr/Payslip/:payrollId' element={<HrPayslip />} />
          <Route path='Hr/SalaryPayments' element={<HrSalaryPayments />} />
          <Route path='Hr/ServiceCharge' element={<HrServiceCharge />} />
          <Route path='Hr/Tips' element={<HrTips />} />
          <Route path='Hr/StaffMeals' element={<HrStaffMeals />} />
          <Route path='Hr/UniformDeposit' element={<HrUniformDeposit />} />
          <Route path='Hr/Dashboard' element={<HrDashboard />} />
          <Route path='Hr/Reports' element={<HrReports />} />
          <Route path='Guestreport' element={<Guestreport />} />
          <Route path='CreateUser' element={<CreateUser />} />
          <Route path='UserPermission' element={<UserPermission />} />
          <Route path='UserReport' element={<UserReport />} />
          <Route path='MultiHotelDashboard' element={<MultiHotelDashboard />} />
          <Route path='StockTransfer' element={<StockTransfer />} />
          <Route path='StockOpeningClosingReport' element={<StockOpeningClosingReport />} />
          <Route path='BookingHistory' element={<BookingHistory />} />
          <Route path='CustomerDueReport' element={<CustomerDueReport />} />
          <Route path='RbacRoles' element={<RbacRoleManagement />} />
          <Route path='RbacUserOverrides' element={<RbacUserOverrides />} />
          <Route path='Agent' element={<Agent />} />
          <Route path='AgentReport' element={<AgentReport />} />
          <Route path='AgentCommision' element={<AgentCommision />} />
          <Route path='Couponmaster' element={<Couponmaster />} />
          <Route path='Addonreport' element={<Addonreport />} />
          <Route path='RoomBookingWizard' element={<RoomBookingWizard />} />
          <Route path='PurchaseEntry' element={<PurchaseEntry />} />
          {/* The property's own website: designed here, served by the
              Webthemebuilder renderer. One page, two tabs - the old
              /WebsiteMenu address still works and opens the Menu tab. */}
          <Route path='WebsiteBuilder' element={<FrontWebsite />} />
          <Route path='WebsiteMenu' element={<FrontWebsite />} />
          <Route
            path='PurchaseLedgerReport'
            element={<PurchaseLedgerReport />}
          />
          {/* Pub / Club workspace - pathless layout route, same pattern as the
              Restaurant Setup and Restro Reports strips. The three routes keep
              their existing paths, so bookmarks and the sidebar link are
              unchanged; they simply gain a tab strip above them. */}
          <Route element={<PubCardLayout />}>
            <Route path='PubCardRequests' element={<PubCardRequests />} />
            <Route path='PubCardReport' element={<PubCardReport />} />
            <Route path='PubCardLedger' element={<PubCardLedger />} />
          </Route>
          <Route path='StorePurchase' element={<StorePurchase />} />
          <Route path='RecipeMaster' element={<RecipeMaster />} />
          <Route path='SupplierMaster' element={<SupplierMaster />} />
          <Route path='ChannelManager' element={<ChannelManager />} />
          <Route path='ResetData' element={<ResetData />} />
          <Route path='StoreLocations' element={<StoreLocations />} />
          <Route path='RoomReservation' element={<RoomReservation />} />
          <Route path='StockDashboard' element={<StockDashboard />} />
          <Route path='PurchaseReports' element={<PurchaseReports />} />
          <Route path='Holdreport' element={<Holdreport />} />
          {/* ---- Restaurant Masters workspace ----------------------------
               Pathless layout route: adds the horizontal tab strip over the
               nine master pages. Paths, components, permissions unchanged. */}
          <Route element={<RestaurantMastersLayout />}>
            <Route path='AddDepartment' element={<AddDepartment />} />
            <Route path='Createarea' element={<Createarea />} />
            <Route path='Category' element={<Category />} />
            <Route path='AddItem' element={<AddItem />} />
            <Route path='VariantManagement' element={<VariantManagement />} />
            <Route path='StoreMaster' element={<StoreMaster />} />
            <Route path='ItemStoreMapping' element={<ItemStoreMapping />} />
            <Route path='AreaPricing' element={<AreaPricing />} />
            <Route path='MenuUploadHistory' element={<MenuUploadHistory />} />
            <Route path='OnlineOrdering' element={<OnlineOrdering />} />
          </Route>
          {/* Not restaurant masters, so no Restaurant Setup tab strip over them. */}
          <Route path='AccountingIntegration' element={<AccountingIntegration />} />
          <Route path='Marketing/Customers' element={<MarketingCustomers />} />
          <Route path='Marketing/Settings' element={<MarketingSettings />} />
          <Route path='Marketing/Loyalty' element={<MarketingLoyalty />} />
          <Route path='Marketing/Campaigns' element={<MarketingCampaigns />} />
          <Route path='Marketing/Automations' element={<MarketingAutomations />} />
          <Route path='Marketing/Dashboard' element={<MarketingDashboard />} />
          {/* Bill Print now lives in Business Settings; the old address lands there. */}
          <Route
            path='BillPrintSettings'
            element={<Navigate to='/admin/BusinessSettings?tab=billprint' replace />}
          />
          {/* ---- Restaurant Orders workspace ----------------------------
               Pathless layout route: horizontal tab strip over the ordering
               pages. Paths, components, permissions unchanged. */}
          <Route element={<RestaurantOrdersLayout />}>
            {/* Both routes render the SAME component, so React would reuse the
                instance and keep its state when moving between the two tabs.
                CreateOrder seeds `step` from `quickBill` at mount only, so a
                reused instance lands on the wrong step (Quick Bill -> Dine In
                opened straight on Items, skipping the table picker) until a
                manual refresh remounted it. Distinct keys force the remount. */}
            <Route path='CreateOrder' element={<CreateOrder key='pos' />} />
            <Route
              path='QuickBill'
              element={<CreateOrder key='quickbill' quickBill />}
            />
            <Route path='Order' element={<Order />} />
            {/* Moved INSIDE the layout so it carries the tab strip.
                Left outside, the tab would navigate to a page with no
                strip on it and the user would be stranded. */}
            <Route path='Reservations' element={<Reservations />} />
            <Route path='TableOrders' element={<TableOrders />} />
            <Route path='QrOrders' element={<QrOrders />} />
            {/* Swiggy / Zomato orders from WeraFoods, beside Delivery. */}
            <Route path='OnlineOrders' element={<OnlineOrdersPage />} />
            <Route path='RestaurantPayments' element={<RestaurantPayments />} />
            <Route path='OrderHistory' element={<OrderHistory />} />
          </Route>
          {/* ---- Restro Report workspace -------------------------------
               Same pathless-layout pattern as Restaurant Orders above: a tab
               strip over the four restaurant reports. The routes, components
               and permissions are the ones that already existed - only the
               navigation around them is new. */}
          <Route element={<RestroReportsLayout />}>
            <Route
              path='RestaurantInvoiceReport'
              element={<RestaurantInvoiceReport />}
            />
            <Route path='RestaurantActivity' element={<RestaurantActivity />} />
            <Route
              path='RestaurantGuestReport'
              element={<RestaurantGuestReport />}
            />
            <Route
              path='RestaurantSaleReport'
              element={<RestaurantSaleReport />}
            />
            <Route path='ZReport' element={<ZReport />} />
            <Route path='PrinterHealth' element={<PrinterHealth />} />
          </Route>
          <Route path='KotReport' element={<KotReport />} />
          <Route path='HourlyBooking' element={<HourlyBooking />} />
          <Route path='ItemReport' element={<ItemReport />} />
          <Route path='ExpiryReport' element={<ExpiryReport />} />
          <Route path='AllCategory' element={<AllCategory />} />
          <Route path='QrCodeGenerator' element={<QrCodeGenerator />} />
          <Route path='BookingLedger' element={<BookingLedger />} />
          <Route path='Customerwallet' element={<Customerwallet />} />
          <Route path='ChangePackage' element={<ChangePackage />} />
          <Route
            path='Agentcommissionreport'
            element={<Agentcommissionreport />}
          />
          <Route
            path='KitchenPendingOrders'
            element={<KitchenPendingOrders />}
          />
          <Route
            path='StockTransactionHistory'
            element={<StockTransactionHistory />}
          />

          {/* ---- Spa POS ---- */}
          <Route path='Spa/Services' element={<SpaServiceMaster />} />
          <Route path='Spa/Therapists' element={<TherapistMaster />} />
          <Route path='Spa/Bookings' element={<SpaBookings />} />

          {/* ---- Laundry POS ---- */}
          <Route path='Laundry/Items' element={<LaundryItemMaster />} />
          <Route path='Laundry/Orders' element={<LaundryOrders />} />

          {/* ---- Mini-Bar POS ---- */}
          <Route path='MiniBar/Items' element={<MiniBarItemMaster />} />
          <Route path='MiniBar/Consumption' element={<MiniBarConsumption />} />

          {/* ---- Housekeeping ---- */}
          <Route
            path='Housekeeping/Dashboard'
            element={<HousekeepingDashboard />}
          />
          <Route
            path='Housekeeping/Staff'
            element={<HousekeepingStaffMaster />}
          />
          <Route path='Housekeeping/Tasks' element={<HousekeepingTasks />} />

          {/* ---- SaaS Subscription ---- */}
          <Route path='Subscription' element={<Subscription />} />
          <Route path='SubscriptionPlans' element={<SubscriptionPlans />} />
          <Route
            path='SubscriptionPaymentReport'
            element={<SubscriptionPaymentReport />}
          />
        </Route>

        <Route
          path='*'
          element={
            <div style={{ padding: '2rem' }}>
              <h1>Page Not Found</h1>
            </div>
          }
        />
      </Routes>
      </Suspense>
    </PermissionProvider>
  );
}
